import { useState, useMemo, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  ClearAll as ClearIcon,
  VpnKey as PasswordIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import ListLayout from "../../../components/Layout/ListLayout";
import { Trans, useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth";
import {
  useListUsersPortalExterno,
  useEnableUserPortalExterno,
  useDisableUserPortalExterno,
  useResendPasswordPortalExterno,
} from "../../../api/bpm/endpoints";
import type { PortalExternoUsuarioDto } from "../../../api/bpm/model";

const portalExternoUsersSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  cpf: z.string().optional(),
  email: z.string().optional(),
  status: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

export const Route = createFileRoute("/__auth/admin/portal-externo-users")({
  component: PortalExternoUsersComponent,
  validateSearch: (search) => portalExternoUsersSearchSchema.parse(search),
  staticData: {
    title: "Usuários do portal externo",
  },
});

const ESTADOS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA",
  "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN",
  "RO", "RR", "RS", "SC", "SE", "SP", "TO"
];

function PortalExternoUsersComponent() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = user?.tenant || "";

  const enableUserMutation = useEnableUserPortalExterno();
  const disableUserMutation = useDisableUserPortalExterno();
  const resendPasswordMutation = useResendPasswordPortalExterno();

  const [filterName, setFilterName] = useState(search.q || "");
  const [filterCpf, setFilterCpf] = useState(search.cpf || "");
  const [filterEmail, setFilterEmail] = useState(search.email || "");
  const [filterStatus, setFilterStatus] = useState(search.status || "Todos");
  const [filterCity, setFilterCity] = useState(search.city || "");
  const [filterState, setFilterState] = useState(search.state || "Todos");

  useEffect(() => {
    setFilterName(search.q || "");
    setFilterCpf(search.cpf || "");
    setFilterEmail(search.email || "");
    setFilterStatus(search.status || "Todos");
    setFilterCity(search.city || "");
    setFilterState(search.state || "Todos");
  }, [search]);

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [passwordResetDialog, setPasswordResetDialog] = useState<{
    open: boolean;
    user: PortalExternoUsuarioDto | null;
  }>({
    open: false,
    user: null,
  });

  const [deactivationReasonDialog, setDeactivationReasonDialog] = useState<{
    open: boolean;
    user: PortalExternoUsuarioDto | null;
  }>({
    open: false,
    user: null,
  });

  const { data: apiData, isLoading } = useListUsersPortalExterno(tenant, {
    page: (search.page || 1) - 1,
    size: search.size || 10,
    name: search.q || undefined,
    cpf: search.cpf || undefined,
    email: search.email || undefined,
    status: search.status !== undefined && search.status !== "Todos" ? (search.status === "Ativo" ? "true" : "false") : undefined,
    city: search.city || undefined,
    state: search.state === "Todos" ? undefined : search.state,
  });

  const portalExternoUserData = apiData as any;
  const filteredUsers = useMemo<PortalExternoUsuarioDto[]>(() => portalExternoUserData?.content || [], [portalExternoUserData]);
  const totalElements = useMemo<number>(() => portalExternoUserData?.totalElements || 0, [portalExternoUserData]);

  const handleApplyFilters = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        q: filterName || undefined,
        cpf: filterCpf || undefined,
        email: filterEmail || undefined,
        status: filterStatus === "Todos" ? undefined : filterStatus,
        city: filterCity || undefined,
        state: filterState === "Todos" ? undefined : filterState,
        page: 1,
      }),
    });
    setToast({
      open: true,
      message: t("common.apply") || "Filtros aplicados com sucesso!",
      severity: "success",
    });
  };

  const handleClearFilters = () => {
    setFilterName("");
    setFilterCpf("");
    setFilterEmail("");
    setFilterStatus("Todos");
    setFilterCity("");
    setFilterState("Todos");

    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });

    setToast({
      open: true,
      message: t("common.clear") || "Filtros limpos!",
      severity: "info",
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage + 1,
      }),
    });
  };

  const handleToggleStatus = (userId: number, currentStatus: boolean) => {
    const mutation = currentStatus ? disableUserMutation : enableUserMutation;
    mutation.mutate(
      { tenant, userId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [`/v1/${tenant}/portal-externo/users`],
          });
          setToast({
            open: true,
            message: currentStatus
              ? "Usuário inativado com sucesso!"
              : "Usuário ativado com sucesso!",
            severity: "success",
          });
        },
        onError: () => {
          setToast({
            open: true,
            message: "Erro ao atualizar status do usuário.",
            severity: "error",
          });
        },
      }
    );
  };

  const handleRequestPasswordReset = (user: PortalExternoUsuarioDto) => {
    setPasswordResetDialog({
      open: true,
      user,
    });
  };

  const handleConfirmPasswordReset = () => {
    if (passwordResetDialog.user?.id) {
      resendPasswordMutation.mutate(
        { tenant, userId: passwordResetDialog.user.id },
        {
          onSuccess: () => {
            setToast({
              open: true,
              message: t("admin.externalPortalUsers.passwordResetSuccess", {
                defaultValue: `Instruções de redefinição enviadas para ${passwordResetDialog.user?.email}!`,
              }),
              severity: "success",
            });
          },
          onError: () => {
            setToast({
              open: true,
              message: "Erro ao enviar instruções de redefinição de senha.",
              severity: "error",
            });
          },
        }
      );
    }
    setPasswordResetDialog({ open: false, user: null });
  };

  const filtersNode = (
    <>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        color="text.secondary"
        sx={{ mb: 2.5, textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {t("admin.externalPortalUsers.classData", { defaultValue: "Dados da classe" })}
      </Typography>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label={t("common.name") || "Nome"}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label="CPF"
            value={filterCpf}
            onChange={(e) => setFilterCpf(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label={t("common.email") || "E-mail"}
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>{t("admin.externalPortalUsers.status") || "Status"}</InputLabel>
            <Select
              value={filterStatus}
              label={t("admin.externalPortalUsers.status") || "Status"}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="Todos">{t("common.all") || "Todos"}</MenuItem>
              <MenuItem value="Ativo">{t("common.active") || "Ativo"}</MenuItem>
              <MenuItem value="Inativo">{t("common.inactive") || "Inativo"}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <TextField
            label={t("admin.externalPortalUsers.city", { defaultValue: "Cidade" })}
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>{t("admin.externalPortalUsers.state", { defaultValue: "Estado" })}</InputLabel>
            <Select
              value={filterState}
              label={t("admin.externalPortalUsers.state", { defaultValue: "Estado" })}
              onChange={(e) => setFilterState(e.target.value)}
            >
              <MenuItem value="Todos">{t("common.all") || "Todos"}</MenuItem>
              {ESTADOS.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={12} display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
            }}
          >
            {t("common.clear") || "Limpar"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={handleApplyFilters}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {t("common.filter") || "Filtrar"}
          </Button>
        </Grid>
      </Grid>
    </>
  );

  return (
    <ListLayout
      title={t("admin.externalPortalUsers.title")}
      subtitle={t("admin.externalPortalUsers.subtitle")}
      filters={filtersNode}
      isEmpty={filteredUsers.length === 0}
      isLoading={isLoading}
    >
      <TableContainer
        component={Paper}
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>
                {t("common.name") || "Nome"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>CPF</TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>
                {t("common.email") || "E-mail"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>
                {t("admin.externalPortalUsers.region")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>
                {t("admin.externalPortalUsers.status") || "Status"}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2 }}>
                {t("admin.externalPortalUsers.registration")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>
                {t("common.actions") || "Ações"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user: PortalExternoUsuarioDto, index) => (
              <TableRow
                key={user.id ?? `user-${index}`}
                hover
                sx={{
                  transition: "background-color 0.2s",
                  "&:hover": { backgroundColor: "action.selected" },
                }}
              >
                <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                  {user.name || "-"}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>{user.document || "-"}</TableCell>
                <TableCell sx={{ py: 1.5, color: "primary.main" }}>
                  {user.email || "-"}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  {user.cityAndState ||
                    (user.city && user.state
                      ? `${user.city} - ${user.state}`
                      : user.city || user.state || "-")}
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Chip
                    label={user.active ? "Ativo" : "Inativo"}
                    color={user.active ? "success" : "error"}
                    size="small"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderRadius: "6px",
                      px: 0.5,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>-</TableCell>
                <TableCell align="center" sx={{ py: 1.5 }}>
                  {user.deactivationDate != null ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<InfoIcon />}
                      onClick={() =>
                        setDeactivationReasonDialog({ open: true, user })
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        borderRadius: 1.5,
                      }}
                    >
                      Motivo Desativação
                    </Button>
                  ) : (
                    <Box display="flex" justifyContent="center" gap={1.5}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<PasswordIcon />}
                        onClick={() => handleRequestPasswordReset(user)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          borderRadius: 1.5,
                          borderColor: "grey.400",
                          color: "text.primary",
                          "&:hover": {
                            borderColor: "text.primary",
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        {t("admin.externalPortalUsers.password")}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color={user.active ? "error" : "primary"}
                        startIcon={
                          user.active ? <ToggleOffIcon /> : <ToggleOnIcon />
                        }
                        onClick={() => handleToggleStatus(user.id!, !!user.active)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          borderRadius: 1.5,
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                        }}
                      >
                        {user.active
                          ? t("admin.externalPortalUsers.inactivate", { defaultValue: "Inativar" })
                          : t("admin.externalPortalUsers.activate", { defaultValue: "Ativar" })}
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalElements}
        page={(search.page || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          navigate({
            search: (prev) => ({
              ...prev,
              size: parseInt(e.target.value, 10),
              page: 1,
            }),
          });
        }}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          mb: 4,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={passwordResetDialog.open}
        onClose={() => setPasswordResetDialog({ open: false, user: null })}
        PaperProps={{
          sx: { borderRadius: 3, px: 1, py: 0.5 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t("admin.externalPortalUsers.password")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>

            <Trans
              i18nKey="admin.externalPortalUsers.passwordResetConfirmation"
              values={{ email: passwordResetDialog.user?.email, name: passwordResetDialog.user?.name }}
              components={{ strong: <strong /> }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => setPasswordResetDialog({ open: false, user: null })}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            {t("common.cancel") || "Cancelar"}
          </Button>
          <Button
            onClick={handleConfirmPasswordReset}
            color="primary"
            variant="contained"
            autoFocus
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            {t("common.confirm") || "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivationReasonDialog.open}
        onClose={() => setDeactivationReasonDialog({ open: false, user: null })}
        PaperProps={{
          sx: { borderRadius: 3, px: 1, py: 0.5, minWidth: 360 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Motivo da desativação
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Usuário:</strong> {deactivationReasonDialog.user?.name || "-"}
            </Typography>
            {deactivationReasonDialog.user?.deactivationDate && (
              <Typography variant="body2" color="text.secondary">
                <strong>Data de desativação:</strong>{" "}
                {new Date(deactivationReasonDialog.user.deactivationDate).toLocaleString("pt-BR")}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Motivo:</strong>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 2 }}>
              <Typography variant="body2" color="text.primary">
                {deactivationReasonDialog.user?.deactivationReason || "Nenhum motivo informado."}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => setDeactivationReasonDialog({ open: false, user: null })}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            {t("common.close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </ListLayout>
  );
}
